/**
 * @fileoverview Audit Record Management Hook
 *
 * Manages the case ingestion audit record including:
 * - Fetching audit record for section tracking
 * - Updating section statuses (found/not_found)
 * - Checking if sections have been reviewed
 * - Computing whether the wizard can be finished
 */
import { skipToken } from '@reduxjs/toolkit/query';
import { useCallback, useMemo } from 'react';

import {
  useGetCaseIngestionQuery,
  useUpdateCaseIngestionSectionsMutation,
} from '@/services/api/caseIngestionService';
import type {
  CaseIngestionResult,
  IngestionSection,
  IngestionSections,
  IngestionSectionStatus,
} from '@/services/types/case-ingestion-types';
import {
  INGESTION_SECTIONS,
  isSectionReviewed,
} from '@/services/types/case-ingestion-types';

export interface UseAuditRecordManagementReturn {
  /** The audit record containing section statuses */
  auditRecord: CaseIngestionResult | null;
  /** Loading state for the audit record */
  isLoadingAudit: boolean;
  /** Update one or more section statuses in the audit record (batch) */
  updateSectionStatuses: (
    sections: Partial<Record<IngestionSection, IngestionSectionStatus | null>>,
  ) => Promise<void>;
  /** Get current section statuses from the audit record */
  getSectionStatuses: () => IngestionSections;
  /** Check if a specific section has been reviewed */
  isSectionReviewed: (section: IngestionSection) => boolean;
  /** Whether all tracked sections have been reviewed */
  canFinish: boolean;
}

/**
 * Hook to manage the case ingestion audit record.
 *
 * Provides functionality to:
 * - Fetch and track the audit record
 * - Update section statuses
 * - Check section review status
 * - Determine if wizard can be completed
 *
 * @param matterId - The matter ID to fetch audit record for (null if no matter yet)
 */
export const useAuditRecordManagement = (
  matterId: string | null,
): UseAuditRecordManagementReturn => {
  // Fetch audit record for section tracking
  const { data: auditRecord, isLoading: isLoadingAudit } =
    useGetCaseIngestionQuery(matterId ?? skipToken);

  const [updateSections] = useUpdateCaseIngestionSectionsMutation();

  /**
   * Update one or more section statuses in the audit record (batch).
   */
  const updateSectionStatuses = useCallback(
    async (
      sections: Partial<Record<IngestionSection, IngestionSectionStatus | null>>,
    ) => {
      if (!matterId) return;

      await updateSections({
        matterId,
        data: {
          sections,
        },
      }).unwrap();
      // Audit record will be updated via RTK Query cache invalidation
    },
    [matterId, updateSections],
  );

  /**
   * Get current section statuses from the audit record.
   */
  const getSectionStatuses = useCallback((): IngestionSections => {
    if (!auditRecord) {
      return {
        first_party_coverage: null,
        third_party_coverage: null,
        health_insurance: null,
        police_report: null,
        medical_providers: null,
        retainer_document: null,
        hipaa_document: null,
        companion_cases: null,
      };
    }
    return auditRecord.sections;
  }, [auditRecord]);

  /**
   * Check if a specific section has been reviewed.
   */
  const checkSectionReviewed = useCallback(
    (section: IngestionSection): boolean => {
      if (!auditRecord) return false;
      return isSectionReviewed(auditRecord.sections, section);
    },
    [auditRecord],
  );

  /**
   * Check if all tracked sections have been reviewed.
   */
  const canFinish = useMemo(() => {
    if (!auditRecord) return false;
    return INGESTION_SECTIONS.every((section) =>
      isSectionReviewed(auditRecord.sections, section),
    );
  }, [auditRecord]);

  return {
    auditRecord: auditRecord ?? null,
    isLoadingAudit,
    updateSectionStatuses,
    getSectionStatuses,
    isSectionReviewed: checkSectionReviewed,
    canFinish,
  };
};
